<section class="mod-page" data-module="complaints">
  <header class="page-header">
    <div>
      <p class="eyebrow">{{ isAssignedOnly() ? 'Assigned complaints' : 'Complaint management' }}</p>
      <h1>{{ isAssignedOnly() ? 'My assigned complaints' : 'All complaints' }}</h1>
    </div>
    <div class="btn-row">
      <button type="button" class="btn primary" (click)="exportCsv()">Export to sheet</button>
    </div>
  </header>

  @if (message()) {
    <p class="toast">{{ message() }}</p>
  }

  @if (!isDm()) {
    <div class="kpi-bar">
      <button
        type="button"
        class="kpi-tile"
        [class.active]="kpiFocus() === 'total' || kpiFocus() === null"
        (click)="selectKpi('total')"
      >
        <span class="kpi-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16v16H4z" /><path d="M8 9h8M8 13h8M8 17h4" /></svg>
        </span>
        <span class="kpi-body">
          <span class="kpi-value">{{ kpiTotal() }}</span>
          <span class="kpi-label">Total complaints</span>
        </span>
      </button>
      <button
        type="button"
        class="kpi-tile t-pending"
        [class.active]="kpiFocus() === 'pending'"
        (click)="selectKpi('pending')"
      >
        <span class="kpi-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>
        </span>
        <span class="kpi-body">
          <span class="kpi-value">{{ kpiPending() }}</span>
          <span class="kpi-label">Pending review</span>
        </span>
      </button>
      <button
        type="button"
        class="kpi-tile t-resolved"
        [class.active]="kpiFocus() === 'resolved'"
        (click)="selectKpi('resolved')"
      >
        <span class="kpi-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 12 2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg>
        </span>
        <span class="kpi-body">
          <span class="kpi-value">{{ kpiResolved() }}</span>
          <span class="kpi-label">Resolved</span>
        </span>
      </button>
    </div>
  }

  @if (!isDm()) {
    <div class="btn-row" role="tablist" aria-label="Complaint records">
      <button
        type="button"
        class="btn"
        role="tab"
        [class.primary]="view() === 'active'"
        [attr.aria-selected]="view() === 'active'"
        (click)="switchView('active')"
      >
        Active Cases
      </button>
      <button
        type="button"
        class="btn"
        role="tab"
        [class.primary]="view() === 'history'"
        [attr.aria-selected]="view() === 'history'"
        (click)="switchView('history')"
      >
        History ({{ kpiResolved() }})
      </button>
    </div>
  }

  <div class="toolbar filter-bar">
    <label style="flex: 1.4">
      Search
      <input [ngModel]="query()" (ngModelChange)="query.set($event); page.set(1)" placeholder="Search all table fields" />
    </label>
    @if (!isDm()) {
      <label>
        Status
        <select [ngModel]="status()" (ngModelChange)="status.set($event); page.set(1)">
          @for (s of visibleStatuses(); track s) {
            <option [value]="s">{{ s }}</option>
          }
        </select>
      </label>
    }
    <label>
      Case type
      <select [ngModel]="caseType()" (ngModelChange)="caseType.set($event); page.set(1)">
        @for (t of caseTypes(); track t) {
          <option [value]="t">{{ t }}</option>
        }
      </select>
    </label>
    @for (col of activeColumnFilters(); track col.key) {
      <label>
        {{ col.label }}
        @if (col.filter === 'select') {
          <select
            [ngModel]="columnFilterValue(col.key)"
            (ngModelChange)="setColumnFilter(col.key, $event)"
          >
            @for (opt of columnSelectOptions()[col.key] || ['All']; track opt) {
              <option [value]="opt">{{ opt }}</option>
            }
          </select>
        } @else {
          <input
            type="text"
            [ngModel]="columnFilterValue(col.key)"
            (ngModelChange)="setColumnFilter(col.key, $event)"
            [placeholder]="'Filter ' + col.label"
          />
        }
      </label>
    }
    @if (!isDm()) {
      <label>
        Assigned role
        <select [ngModel]="assignedRole()" (ngModelChange)="assignedRole.set($event); page.set(1)">
          @for (r of assignedRoleOptions(); track r) {
            <option [value]="r">{{ r }}</option>
          }
        </select>
      </label>
    }
    <label>
      From
      <input type="date" [ngModel]="dateFrom()" (ngModelChange)="dateFrom.set($event); page.set(1)" />
    </label>
    <label>
      To
      <input type="date" [ngModel]="dateTo()" (ngModelChange)="dateTo.set($event); page.set(1)" />
    </label>
  </div>

  <div class="table-toolbar">
    <span class="muted">{{ filtered().length }} record{{ filtered().length === 1 ? '' : 's' }}</span>
    <div class="table-toolbar-actions">
      @if (hasActiveFilters()) {
        <button type="button" class="btn" (click)="resetFilters()">Clear All</button>
      }
      <div class="column-picker">
        <button
          type="button"
          class="btn"
          [attr.aria-expanded]="columnPickerOpen()"
          (click)="toggleColumnPicker()"
        >
          + Columns
          @if (enabledExtraColumns().size > 0) {
            <span class="count-badge">{{ enabledExtraColumns().size }}</span>
          }
        </button>
        @if (columnPickerOpen()) {
          <div class="column-picker-backdrop" (click)="toggleColumnPicker()"></div>
          <div class="column-picker-menu" role="dialog" aria-label="Add columns">
            <div class="column-picker-head">
              <p class="column-picker-title">Add fields to the table</p>
              <button type="button" class="column-picker-close" (click)="toggleColumnPicker()">Done</button>
            </div>
            <div class="column-picker-list">
              @for (col of extraColumnDefs; track col.key) {
                <label class="column-option">
                  <input type="checkbox" [checked]="isColumnEnabled(col.key)" (change)="toggleColumn(col.key)" />
                  {{ col.label }}
                </label>
              }
            </div>
          </div>
        }
      </div>
    </div>
  </div>

  <div class="table-wrap" tabindex="0" aria-label="Complaints table; scroll horizontally to see all columns">
    <table>
      <colgroup>
        <col style="width: 4rem" />
        <col style="width: 6rem" />
        @for (col of visibleColumns(); track col.key) {
          <col [style.width]="col.width" />
        }
        <col style="width: 6.5rem" />
        @if (!isDm()) {
          <col style="width: 8rem" />
        }
        <col style="width: 9.5rem" />
      </colgroup>
      <thead>
        <tr>
          <th>S.No.</th>
          <th>ID</th>
          @for (col of visibleColumns(); track col.key) {
            <th>{{ col.label }}</th>
          }
          <th>Date</th>
          @if (!isDm()) {
            <th>Status</th>
          }
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        @for (item of paged(); track item.id) {
          <tr>
            <td>{{ serialNo(item) }}</td>
            <td class="truncate"><strong [attr.title]="item.trackingId">{{ item.trackingId }}</strong></td>
            @for (col of visibleColumns(); track col.key) {
              <td class="truncate" [attr.title]="col.get(item)">{{ col.get(item) }}</td>
            }
            <td>{{ fmtDate(item.submittedAt) }}</td>
            @if (!isDm()) {
              <td class="truncate">
                <span [class]="'pill status-' + statusTone(item.status)" [attr.title]="item.status">{{ item.status }}</span>
              </td>
            }
            <td>
              <div class="row-actions">
                <a class="btn btn-sm view-btn" [routerLink]="['/complaints', item.id]">
                  <span class="view-btn-text">View detail</span>
                  <svg class="view-btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M5 12h13" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </a>
              </div>
            </td>
          </tr>
        } @empty {
          <tr>
            <td [attr.colspan]="visibleColumns().length + (isDm() ? 4 : 5)" class="muted" style="text-align: center; padding: 1.5rem">
              No complaints match filters.
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>

  <div class="toolbar" style="justify-content: space-between">
    <span class="muted">Showing page {{ page() }} of {{ totalPages() }} · {{ filtered().length }} records</span>
    <div class="btn-row">
      <button type="button" class="btn" [disabled]="page() <= 1" (click)="page.set(page() - 1)">Previous</button>
      <button type="button" class="btn" [disabled]="page() >= totalPages()" (click)="page.set(page() + 1)">Next</button>
    </div>
  </div>
</section>
